import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useLocation, Link } from "wouter";
import {
  Check,
  X,
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  ChevronLeft,
} from "lucide-react";

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email"),
});

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        passwordRegex,
        "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character",
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const PasswordStrengthIndicator = ({ password }) => {
  const checks = [
    { label: "At least 8 characters", test: (pwd) => pwd.length >= 8 },
    { label: "At least 1 uppercase letter", test: (pwd) => /[A-Z]/.test(pwd) },
    { label: "At least 1 lowercase letter", test: (pwd) => /[a-z]/.test(pwd) },
    { label: "At least 1 number", test: (pwd) => /\d/.test(pwd) },
    {
      label: "At least 1 special character",
      test: (pwd) => /[@$!%*?&]/.test(pwd),
    },
  ];

  return (
    <div className="mt-2 space-y-1">
      {checks.map((check, index) => {
        const isValid = check.test(password);
        return (
          <div key={index} className="flex items-center text-sm">
            {isValid ? (
              <Check className="h-3 w-3 text-green-500 mr-2" />
            ) : (
              <X className="h-3 w-3 text-red-500 mr-2" />
            )}
            <span className={isValid ? "text-green-600" : "text-red-600"}>
              {check.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ResetPasswordPage = () => {
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState("");

  useEffect(() => {
    // Get token from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get("token");

    if (tokenParam) {
      console.log("Token from URL:", tokenParam);
      setToken(tokenParam);
    }
  }, []);

  // Forgot Password State
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Reset Password State
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const [, setLocation] = useLocation();

  const handleBackClick = () => {
    // Navigate back to login/auth page
    setLocation("/landing-page");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setEmailError("");
    setEmailLoading(true);

    try {
      const validatedData = forgotPasswordSchema.parse({ email });

      console.log("Sending forgot password request for:", validatedData.email);

      // API call to forgot password endpoint
      const response = await fetch("/api/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: validatedData.email,
        }),
      });

      console.log("Forgot password response status:", response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Forgot password response:", responseData);
        setEmailSent(true);
      } else {
        const errorData = await response.json();
        console.error("Forgot password error:", errorData);
        setEmailError(errorData.message || "Failed to send reset email");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      if (error instanceof z.ZodError) {
        setEmailError(error.errors[0].message);
      } else {
        setEmailError("An unexpected error occurred");
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setConfirmPasswordError("");
    setTokenError("");
    setResetLoading(true);

    try {
      const validatedData = resetPasswordSchema.parse({
        newPassword,
        confirmPassword,
      });

      console.log("Sending reset password request with token:", token);

      // API call to reset password endpoint
      const response = await fetch("/api/reset-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: token,
          newPassword: validatedData.newPassword,
          confirmPassword: validatedData.confirmPassword,
        }),
      });

      console.log("Reset password response status:", response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Reset password response:", responseData);
        setResetSuccess(true);
        // setLocation("/auth")
      } else {
        const errorData = await response.json();
        console.error("Reset password error:", errorData);

        // Handle specific error cases
        if (errorData.message.includes("Invalid or expired reset token")) {
          setTokenError(
            "This reset link has expired or is invalid. Please request a new one.",
          );
        } else {
          setPasswordError(errorData.message || "Failed to reset password");
        }
      }
    } catch (error) {
      console.error("Reset password error:", error);
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          if (err.path.includes("confirmPassword")) {
            setConfirmPasswordError(err.message);
          } else {
            setPasswordError(err.message);
          }
        });
      } else {
        setPasswordError("An unexpected error occurred");
      }
    } finally {
      setResetLoading(false);
    }
  };

  // If no token, show forgot password form
  if (!token) {
    return (
      <div className="min-h-screen bg-[#eaf1f3]">
        {/* Back Button - Top Left of Page */}
        <button
          onClick={handleBackClick}
          className="flex items-center text-[#1e1e1e] hover:text-gray-900 transition-colors pt-4 ml-4"
        >
          <ChevronLeft className="h-5 w-5 mr-2" />
          <span className="text-lg text-[#4c7375] font-medium">Back</span>
        </button>

        <div className="flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
          <div className="max-w-md w-full space-y-12">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-[#4c7375] mb-6">
                Forgot Password?
              </h2>
              <p className="text-sm text-[#1e1e1e] leading-relaxed">
                Please enter your email where we'll send a reset password link
              </p>
            </div>

            {emailSent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex">
                  <Check className="h-5 w-5 text-green-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      Reset link sent!
                    </p>
                    <p className="mt-2 text-sm text-green-700 leading-relaxed">
                      Reset password link has been sent to your email. Please
                      check
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="space-y-3">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#76ADAA]"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleForgotPassword(e)
                      }
                      className={`appearance-none block w-full pl-10 pr-3 py-3 border ${
                        emailError ? "border-red-300" : "border-gray-300"
                      } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm`}
                      placeholder="Enter your email"
                    />
                  </div>
                  {emailError && (
                    <p className="mt-2 text-sm text-red-600">{emailError}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={emailLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#4c7375] hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {emailLoading ? "Sending..." : "Send Reset Link"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // If token exists, show reset password form
  return (
    <div className="min-h-screen bg-gray-50 pt-0 px-4 sm:px-6 lg:px-8">

      <div className="flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
        <div className="max-w-md w-full space-y-12">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-[#4c7375] mb-6">
              Reset Password
            </h2>
            <p className="text-sm text-[#1e1e1e] leading-relaxed">
              Enter your new password below
            </p>
          </div>

          {tokenError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    Invalid Reset Link
                  </p>
                  <p className="mt-2 text-sm text-red-700 leading-relaxed">
                    {tokenError}
                  </p>
                </div>
              </div>
            </div>
          )}

          {resetSuccess ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex">
                <Check className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    Password reset successful!
                  </p>
                  <p className="mt-2 text-sm text-green-700 leading-relaxed">
                    Your password has been reset successfully. please log in
                    again
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="space-y-3">
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-[#76ADAA]"
                >
                  New Password
                </label>
                <div
                  className={`relative flex border rounded-lg ${
                    passwordError ? "border-red-300" : "border-gray-300"
                  }`}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleResetPassword(e)
                    }
                    className="appearance-none block w-full pl-10 pr-12 py-3 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center z-20"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-2 text-sm text-red-600">{passwordError}</p>
                )}
                {newPassword && (
                  <PasswordStrengthIndicator password={newPassword} />
                )}
              </div>

              <div className="space-y-3">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-[#76ADAA]"
                >
                  Confirm Password
                </label>
                <div
                  className={`relative flex border rounded-lg ${
                    confirmPasswordError ? "border-red-300" : "border-gray-300"
                  }`}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleResetPassword(e)
                    }
                    className="appearance-none block w-full pl-10 pr-12 py-3 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center z-20"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="mt-2 text-sm text-red-600">
                    {confirmPasswordError}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resetLoading || tokenError}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#4c7375] hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resetLoading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
